import type { ReactNode } from "react";
import {
  ThemeProvider as ThemerProvider,
  useTheme as useThemer,
} from "@norkron/web-themer/react";

/**
 * The app's theme seam over `@norkron/web-themer/react`.
 *
 * This file used to be a 92-line "parity shim" that carried its own OS-preference
 * listener, its own persistence, its own `<html>` attribute write and its own
 * React context, under a note saying to replace it "once the package resolves".
 * The package already resolved. Its claim that the hook surface was
 * "intentionally identical" was also false: the real hook returns
 * `preference` / `resolved` / `setPreference`; the shim returned
 * `theme` / `resolved` / `setTheme` / `toggle`.
 *
 * Everything the shim hand-rolled is now the lib's, including `toggle` — which
 * was pushed UPSTREAM (web-themer 0.15.0) rather than re-derived here, because
 * the rule it encodes is generic and non-obvious: a toggle must resolve first and
 * then invert, never cycle through `"auto"`, or on a dark-mode machine it sets
 * `"dark"` on an already-dark page and reads as broken. Every themed consumer was
 * rewriting that decision. The same upstream change fixed a defect that would
 * otherwise have made this migration a REGRESSION: `useTheme` nulled its instance
 * ref in the effect cleanup, and StrictMode's simulated remount (which `main.tsx`
 * triggers) left the setter permanently dead in development.
 *
 * So the only thing left here is the one genuinely elaborate-specific fact:
 *
 *   `storageKey: "ela-theme"`. The lib defaults to `"norkron-theme"`. The app's
 *   key is `"ela-theme"`, and `index.html`'s pre-paint bootstrap reads that same
 *   key — the two MUST agree or first paint disagrees with React and the user
 *   sees a light-to-dark flash on every load.
 *
 * ## Why there is no React context here
 *
 * The lib's `ThemeProvider` is a Fragment, not a provider: each `useTheme()` call
 * site owns its own themer instance. They stay coherent because `subscribeTheme`
 * keeps a MODULE-LEVEL listener pool that every instance broadcasts into, so a
 * `setPreference` anywhere re-renders every consumer. The provider is still
 * mounted at the root — that is what guarantees `data-theme` exists before any
 * consumer asks — but it carries no value, which is why the storage key has to be
 * passed at BOTH the provider and every hook call, and why this module exists at
 * all rather than consumers importing the lib directly.
 *
 * ## The consumers
 *
 * `shell/AppShell`, `shell/BrandWordmark`, `shell/WorkspaceSwitcher`,
 * `screens/Settings` and `screens/WorkspacePicker` read `resolved`;
 * `shell/UserMenu` also calls `toggle`. Nothing reads `preference` yet — it is
 * re-exported because a three-state control (Light / Dark / Match system) needs
 * it and cannot be built from `resolved` alone.
 */

/** localStorage key. Shared with the `index.html` pre-paint bootstrap. */
export const THEME_STORAGE_KEY = "ela-theme";

/**
 * The themes elaborate ships — declared to the lib, not just to TypeScript.
 *
 * The lib's `ResolvedTheme` is `'light' | 'dark' | (string & {})`, deliberately
 * open for products that ship a third theme (sepia, high-contrast). elaborate
 * ships exactly two, and its consumers index by them: `shell/BrandWordmark`
 * keys a `Record<"light" | "dark", string>` of wordmark assets and
 * `shell/AppShell` passes the resolved theme into a two-case function.
 *
 * Passing `themes` makes that narrowing TRUE AT RUNTIME rather than an assertion
 * over a wider type: `initThemer` rejects any preference outside this list (plus
 * `"auto"`) both from `setPreference` AND when reading the persisted value, so a
 * stale or hand-edited `ela-theme: "sepia"` falls back to `"auto"` instead of
 * being written to `<html data-theme>` where it would match no CSS block and
 * leave the app unstyled. Without it the cast below would be a lie.
 */
export const THEMES = ["light", "dark"] as const;

/** What the user picked. `"auto"` follows the OS. */
export type ThemePreference = (typeof THEMES)[number] | "auto";
/** What is actually on `<html data-theme>`. Never `"auto"`. */
export type ResolvedTheme = (typeof THEMES)[number];

export interface ThemeState {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (p: ThemePreference) => void;
  /** Flip to the opposite of the currently RESOLVED theme. Lib-owned. */
  readonly toggle: () => void;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemerProvider storageKey={THEME_STORAGE_KEY} themes={[...THEMES]}>
      {children}
    </ThemerProvider>
  );
}

export function useTheme(): ThemeState {
  // The single narrowing cast in the app's theme layer, and the only reason this
  // module exists rather than consumers importing the lib directly. It is sound
  // because `themes` above makes the lib enforce the same set at runtime.
  return useThemer({
    storageKey: THEME_STORAGE_KEY,
    themes: [...THEMES],
  }) as ThemeState;
}
